import { computed, ref } from "vue";
import { defineStore } from "pinia";
import api from "../api";
import { Role } from "../enums/Role";
import { isLiveSwitchEmail } from "../helpers/utilities";
import { isRoleOrInRoles } from "../api/account";

type AppRole = {
	id: string;
	name: string;
};

export const useRolesStore = defineStore("roles", () => {
	const _roles = ref<AppRole[]>([]);
	const _isLoaded = ref(false);
	let _loadingPromise: Promise<void> | null = null;

	const roles = computed(() => _roles.value);
	const isLoaded = computed(() => _isLoaded.value);

	const administratorRoleId = computed(() => {
		return _roles.value.find((role) => isRoleOrInRoles(role.name, Role.Administrator))?.id ?? "";
	});

	const viewOnlyRoleId = computed(() => {
		return _roles.value.find((role) => isRoleOrInRoles(role.name, Role.ViewOnly))?.id ?? "";
	});

	const userRoleId = computed(() => {
		return _roles.value.find((role) => isRoleOrInRoles(role.name, Role.User))?.id ?? "";
	});

	const supportRoleId = computed(() => {
		return _roles.value.find((role) => isRoleOrInRoles(role.name, Role.Support))?.id ?? "";
	});

	const superAdministratorRoleId = computed(() => {
		return _roles.value.find((role) => isRoleOrInRoles(role.name, Role.SuperAdministrator))?.id ?? "";
	});

	/**
	 * Checks if a user is eligible to be a billing admin.
	 * @param roleName - The name of the user's role to check.
	 * @param email - The email of the user to check.
	 * @param isNewSubscription - Whether the user is on a new subscription.
	 * @returns True if the user is eligible to be a billing admin, false otherwise.
	 */
	function isBillingAdminEligible(roleName?: string | null, email?: string | null, isNewSubscription = true): boolean {
		if (!isNewSubscription || !roleName) {
			return false;
		}
		if (isLiveSwitchEmail(email)) {
			return false;
		}
		return isRoleOrInRoles(roleName, [Role.Administrator, Role.ViewOnly]);
	}

	async function ensureLoaded(): Promise<void> {
		if (_isLoaded.value) {
			return;
		}
		if (_loadingPromise) {
			await _loadingPromise;
			return;
		}
		_loadingPromise = (async () => {
			const loadedRoles = await api.getRoles();
			_roles.value = Array.isArray(loadedRoles) ? loadedRoles : [];
			_isLoaded.value = true;
		})();
		try {
			await _loadingPromise;
		} finally {
			_loadingPromise = null;
		}
	}

	return {
		roles,
		isLoaded,
		administratorRoleId,
		viewOnlyRoleId,
		userRoleId,
		supportRoleId,
		superAdministratorRoleId,
		isBillingAdminEligible,
		ensureLoaded
	};
});
